import { inject, Injectable } from '@angular/core';

import { AvailableEntries, DeviceOrigin, MenuStage, MenuStageName, NavigationLink } from '../models';
import { ConfigSettingsService } from '../providers';
import { Filter } from '../types';
import { NAV_LINK } from './navigation-link-token';

@Injectable({
  providedIn: 'root',
})
export class NavigationLinkRegistryService {
  private readonly configService = inject(ConfigSettingsService);
  private readonly navLinks: NavigationLink[] = inject(NAV_LINK);
  private readonly registry: Map<AvailableEntries, NavigationLink>;

  constructor() {
    this.registry = new Map(this.navLinks.map((link) => [link.feature, link]));
  }

  public async getMenuItemsForStage(stage: MenuStage): Promise<Array<NavigationLink[]> | undefined> {
    const stageName = MenuStage[stage] as MenuStageName;
    const menu = this.configService.appSettings.customizing?.menu?.[stageName] ?? null;
    if (!menu) {
      console.warn(`no menu configured for stage ${stageName}`);
      return undefined;
    }

    const items = await this.mapToEntries(menu, stage);
    return Array.from(NavigationLinkRegistryService.splitArray(items, (item) => item === null));
  }

  public async getFooterLinks(stage: MenuStage, origin: DeviceOrigin): Promise<NavigationLink[]> {
    const footerNavigation = this.configService.appSettings.customizing?.footerNavigation;
    let links: AvailableEntries[] = footerNavigation
      ? footerNavigation[MenuStage[stage] as keyof typeof footerNavigation]
      : [];
    if (!links?.length) {
      links =
        origin === DeviceOrigin.App
          ? ['PRIVACY', 'CONTACT', 'FAQ']
          : ['CONTACT', 'IMPRESSUM', 'PRIVACY', 'DOWNLOAD', 'VIDEOS', 'FAQ', 'ANDROID_APP', 'IOS_APP'];

      console.warn(`no footer links configured for ${stage.toString()}, taking default`);
    }

    return await this.mapToEntries(links, stage);
  }

  public async mapSingleTileToEntry(item: AvailableEntries): Promise<NavigationLink> {
    const [first] = await this.mapToEntries([item], MenuStage.SIGNED_IN);
    if (!first) {
      console.error(`no entry found for tile ${item}`);
    }
    return first;
  }

  private async mapToEntries(items: AvailableEntries[], stage: MenuStage): Promise<NavigationLink[]> {
    return items
      .filter((item) => item.startsWith('--') || this.registry.has(item))
      .map((item) => this.registry.get(item) ?? null)
      .filter((entry) => entry?.allowedStages === undefined || (entry.allowedStages & stage) > 0)
      .filter((entry) => entry?.guard?.() ?? true)
      .filter((entry): entry is NavigationLink => entry !== null);
  }

  private static *splitArray<T>(items: Array<T>, filter: Filter<T>): IterableIterator<T[]> {
    let section: T[] = [];
    for (const item of items) {
      if (filter(item)) {
        yield section;
        section = [];
        continue;
      }

      section.push(item);
    }

    yield section;
  }
}
